/**
 * Chat Input Component
 *
 * Input field with send button for the chat widget.
 * CP Admin 2077 styling with orange accents.
 * Includes optional voice input button for Gemini Live API.
 */

import React, { useState, useRef, useEffect } from 'react';
import { Send } from 'lucide-react';
import { VoiceMicButton } from '../voice/VoiceMicButton';

interface ChatInputProps {
  onSend: (message: string) => void;
  disabled?: boolean;
  placeholder?: string;
  showVoice?: boolean;
}

export function ChatInput({ onSend, disabled = false, placeholder = 'Ask about your inventory...', showVoice = true }: ChatInputProps) {
  const [message, setMessage] = useState('');
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Focus input when component mounts
  useEffect(() => {
    if (inputRef.current && !disabled) {
      inputRef.current.focus();
    }
  }, [disabled]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (message.trim() && !disabled) {
      onSend(message.trim());
      setMessage('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Submit on Enter (without Shift)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  // Auto-resize textarea
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
      inputRef.current.style.height = `${Math.min(inputRef.current.scrollHeight, 120)}px`;
    }
  }, [message]);

  return (
    <form onSubmit={handleSubmit} className="relative">
      <div className="flex items-end gap-2 p-3 bg-slate-900 border-t border-slate-700">
        <div className="flex-1 relative">
          <textarea
            ref={inputRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            rows={1}
            className={`
              w-full px-4 py-3 pr-12
              bg-slate-800 border border-slate-600
              text-slate-100 placeholder-slate-500
              focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500/50
              resize-none overflow-hidden
              transition-colors duration-200
              ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
            `}
            style={{
              clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 0 100%)',
            }}
          />
          {/* Character hint */}
          <div className="absolute bottom-1 right-3 text-[10px] text-slate-600">
            {message.length > 0 && `${message.length}/1000`}
          </div>
        </div>

        {/* Voice Input Button */}
        {showVoice && (
          <VoiceMicButton size="md" />
        )}

        {/* Send Button */}
        <button
          type="submit"
          disabled={disabled || !message.trim()}
          className={`
            flex items-center justify-center
            w-12 h-12
            bg-gradient-to-r from-orange-500 to-orange-600
            text-white
            hover:from-orange-400 hover:to-orange-500
            active:from-orange-600 active:to-orange-700
            disabled:from-slate-600 disabled:to-slate-700 disabled:cursor-not-allowed
            transition-all duration-200
            shadow-lg shadow-orange-500/25
            disabled:shadow-none
          `}
          style={{
            clipPath: 'polygon(0 0, 100% 0, 100% calc(100% - 6px), calc(100% - 6px) 100%, 0 100%)',
          }}
          aria-label="Send message"
        >
          <Send className="w-5 h-5" />
        </button>
      </div>

      {/* Hint text */}
      <div className="px-4 py-1 bg-slate-900 text-[10px] text-slate-600">
        Press <kbd className="px-1 py-0.5 bg-slate-800 rounded text-slate-400">Enter</kbd> to send,
        <kbd className="px-1 py-0.5 bg-slate-800 rounded text-slate-400 ml-1">Shift+Enter</kbd> for new line
      </div>
    </form>
  );
}

export default ChatInput;
